import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import ConfirmationDialog from './ui/ConfirmationDialog';
import { ApiService } from '../utils/api';
import { API_CONFIG } from '../config';
import { useToast } from './Toast';

const CopyField = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded bg-gray-100 px-2 py-1 text-xs">{value}</code>
      <Button type="button" variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(value)}>
        <Copy className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);

/** Cloud side of LAN mode: the key a venue's LAN server uses to import this event and sync back. */
const LanSyncKeyPanel = ({ eventId }) => {
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const [syncKey, setSyncKey] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  const { data: status } = useQuery({
    queryKey: ['syncKey', eventId],
    queryFn: () => ApiService.getSyncKeyStatus(eventId),
    enabled: !!eventId,
  });

  const rotate = async () => {
    setRotating(true);
    try {
      const result = await ApiService.rotateSyncKey(eventId);
      setSyncKey(result.syncKey);
      queryClient.invalidateQueries({ queryKey: ['syncKey', eventId] });
    } catch (err) {
      showError(err.message || 'Could not generate the sync key');
    } finally {
      setRotating(false);
      setConfirmOpen(false);
    }
  };

  if (!status) return null;
  if (!status.enabled) {
    return (
      <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        <p className="font-medium text-gray-900">Venue LAN server is not available on this server</p>
        <p>
          A LAN server is a laptop or mini-PC at the venue running its own copy of the app and database, so desks and
          scanners keep working without internet and sync with the cloud whenever it is back.
        </p>
        <p className="text-xs text-gray-500">
          To enable it, the cloud backend needs <code>OFFLINE_SYNC_ENABLED=true</code>. Ask the NextWave team, then come
          back here to generate the sync key.
        </p>
      </div>
    );
  }

  const configured = !!status.configured;

  return (
    <div className="space-y-2">
      <Label>Venue LAN server</Label>
      <p className="text-xs text-gray-500">
        For venues with unreliable internet: a LAN server imports this event with the key below, serves
        all desks locally and syncs with the cloud whenever internet is available.
      </p>
      {syncKey ? (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <CopyField label="Cloud URL" value={API_CONFIG.baseUrl || window.location.origin} />
          <CopyField label="Event ID" value={eventId} />
          <CopyField label="Sync key" value={syncKey} />
          <p className="text-xs text-amber-800">Copy the key now; it is not shown again.</p>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={rotating}
          onClick={() => (configured ? setConfirmOpen(true) : rotate())}
        >
          <KeyRound className="w-4 h-4 mr-1.5" />
          {configured ? 'Generate new sync key' : 'Generate sync key'}
        </Button>
      )}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={rotate}
        title="Replace sync key?"
        message="Any LAN server using the current key stops syncing until it is re-linked with the new key."
        confirmText="Generate new key"
        variant="warning"
        isLoading={rotating}
      />
    </div>
  );
};

export default LanSyncKeyPanel;
